import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, BarChart3, Clock } from "lucide-react";
import Footer from "@/components/Footer";

interface ScoreBreakdown {
  website: number;
  google: number;
  paidAds: number;
  social: number;
  tracking: number;
  budgetVsProduction: number;
  total: number;
  grade: string;
}

const ThankYouPage = () => {
  const location = useLocation();
  const state = location.state as { score?: ScoreBreakdown; email?: string } | null;
  const score = state?.score;
  const email = state?.email;

  // Fire CompleteRegistration via GTM dataLayer on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: "CompleteRegistration",
        content_name: "Growth Analysis",
        value: score?.total ?? 0,
        currency: "USD",
      });
    }
  }, [score]);

  const categories = score
    ? [
        { label: "Website", score: score.website, max: 20 },
        { label: "Google / SEO / Maps", score: score.google, max: 20 },
        { label: "Paid Ads", score: score.paidAds, max: 20 },
        { label: "Social & Content", score: score.social, max: 10 },
        { label: "Tracking & Follow-Up", score: score.tracking, max: 20 },
        { label: "Budget vs Production", score: score.budgetVsProduction, max: 10 },
      ]
    : [];

  const isQualified = score && (score.grade === "A" || score.grade === "B");

  return (
    <div className="min-h-screen bg-hero-bg">
      <section className="py-16 md:py-24">
        <div className="max-w-2xl mx-auto section-padding">
          {score ? (
            <>
              {/* Score header */}
              <div className="text-center mb-8">
                <BarChart3 className="w-12 h-12 text-accent mx-auto mb-4" />
                <h1 className="text-2xl md:text-3xl font-bold text-hero-foreground mb-2">
                  Your Practice Growth Score
                </h1>
                <div className="flex items-center justify-center gap-4 mb-4">
                  <span className="text-6xl font-bold text-accent">{score.total}</span>
                  <span className="text-3xl font-bold text-hero-foreground/40">/100</span>
                  <span
                    className={`text-2xl font-bold px-4 py-1 rounded-lg ${
                      score.grade === "A"
                        ? "bg-green-500/20 text-green-400"
                        : score.grade === "B"
                        ? "bg-yellow-500/20 text-yellow-400"
                        : score.grade === "C"
                        ? "bg-orange-500/20 text-orange-400"
                        : "bg-red-500/20 text-red-400"
                    }`}
                  >
                    Grade {score.grade}
                  </span>
                </div>
                <p className="text-hero-foreground/60 text-sm">
                  {score.total >= 80
                    ? "Strong — you're ahead of most practices"
                    : score.total >= 60
                    ? "Stable — but you're leaking patients in key areas"
                    : score.total >= 40
                    ? "Underperforming — significant growth opportunities exist"
                    : "Broken system — you're leaving serious money on the table"}
                </p>
              </div>

              {/* Score breakdown */}
              <div className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-lg mb-6">
                <p className="font-semibold text-foreground mb-4">Score Breakdown</p>
                <div className="space-y-4">
                  {categories.map((cat) => {
                    const pct = Math.round((cat.score / cat.max) * 100);
                    return (
                      <div key={cat.label}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-foreground/80">{cat.label}</span>
                          <span className="font-semibold text-foreground">
                            {cat.score}/{cat.max}
                          </span>
                        </div>
                        <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-700 ${
                              pct >= 70 ? "bg-green-500" : pct >= 40 ? "bg-yellow-500" : "bg-accent"
                            }`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 90-day roadmap notice */}
              <div className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-lg mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="font-bold text-foreground mb-1">
                      Your 90-Day Growth Roadmap Is on the Way
                    </p>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      We're building a personalized 90-day action plan based on your answers. It will
                      be sent to{" "}
                      <span className="font-semibold text-accent">{email}</span> shortly — including
                      the exact steps to close the gaps above and start adding new patients.
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-lg text-center">
                {isQualified ? (
                  <>
                    <CheckCircle2 className="w-10 h-10 text-accent mx-auto mb-3" />
                    <h2 className="text-lg font-bold text-foreground mb-2">
                      Want to Fix These Gaps Faster?
                    </h2>
                    <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                      Your roadmap will give you the what. On a Growth Call, we'll walk through the
                      how — including exactly which leaks to fix first for the fastest ROI.
                    </p>
                    <a
                      href="https://api.leadconnectorhq.com/widget/bookings/meetwithhans"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-8 py-4 rounded-lg text-base font-semibold hover:bg-accent/90 transition-all shadow-lg shadow-accent/25"
                    >
                      Book Your Growth Call
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-10 h-10 text-accent mx-auto mb-3" />
                    <h2 className="text-lg font-bold text-foreground mb-2">Your Roadmap Is Coming</h2>
                    <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                      Check your inbox for your personalized 90-day plan. It covers the exact fixes
                      for each gap above, plus a free Growth Guide with proven strategies for
                      practices at your stage.
                    </p>
                    <a
                      href="https://api.leadconnectorhq.com/widget/bookings/meetwithhans"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-8 py-4 rounded-lg text-base font-semibold hover:bg-accent/90 transition-all shadow-lg shadow-accent/25"
                    >
                      Book a Free Growth Call
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </>
                )}
              </div>

              <p className="text-hero-foreground/30 text-xs text-center mt-4">
                Your information is confidential and never shared.
              </p>
            </>
          ) : (
            /* Fallback if someone lands here directly */
            <div className="text-center py-20">
              <CheckCircle2 className="w-16 h-16 text-accent mx-auto mb-6" />
              <h1 className="text-2xl md:text-3xl font-bold text-hero-foreground mb-3">
                Thank You!
              </h1>
              <p className="text-hero-foreground/60 mb-8 max-w-md mx-auto">
                Your Practice Growth Analysis has been submitted. Check your inbox for your
                personalized 90-day roadmap.
              </p>
              <a
                href="https://api.leadconnectorhq.com/widget/bookings/meetwithhans"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-8 py-4 rounded-lg text-base font-semibold hover:bg-accent/90 transition-all shadow-lg shadow-accent/25"
              >
                Book Your Growth Call
                <ArrowRight className="w-4 h-4" />
              </a>
              <div className="mt-6">
                <Link to="/apply" className="text-accent text-sm hover:underline">
                  ← Back to Growth Analysis
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default ThankYouPage;
